import {
  createContext,
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  FeedTblRow,
  MediaColumn,
  STEP_CREATE_BATCH,
  STEP_CREATE_BATCH_V2,
  StepStatus,
  TAssetBatch,
  TComposition,
  TConditionType,
} from "shared/types/assetExporter";
import { ITemplate } from "shared/types/designStudio";
import { useGetMediaColumns } from "../hooks/useGetMediaColumns";
import {
  FiltersType,
  TBackgroundMedia,
  TMediaResizeType,
  TVariable,
} from "../types";
import { isValidApplyCondition, isValidDynamicText } from "../validators";
import {
  getBackgroundResizeType,
  parseCompositionToVariables,
  parseConditions,
  removeItemByCompositionId,
  replaceBackground,
} from "./AssetBatchesContext.utils";
import { fabric } from "fabric";
import { createVideoElement } from "utils/media/utils.input";
import { getImageObjectWithResizeType } from "../../assetBatchDrawer/dynamicText/utils.fabric";

export type TRuleCondition = {
  index: number;
  operator: "and" | "or";
  columnName: string;
  comparisonOperator: TConditionType;
  value: string;
};

export const getEmptyRuleCondition = (index: number) => {
  return {
    index,
    operator: "and",
    columnName: "",
    comparisonOperator: "Equals",
    value: "",
  } as TRuleCondition;
};

interface ContextProps {
  loading: boolean;
  previewLoading: boolean;
  selectedTemplate?: ITemplate;
  searchValue: string;
  variables: Array<TCompositionVariables>;
  columns: string[];
  rows: FeedTblRow[];
  feedId: string;
  ruleConditions: TRuleCondition[];
  previewCount: number;
  batchName: string | undefined;
  currentGroupNum: number;
  hasFixedBackground: boolean;
  backgroundMedias?: Record<string, TBackgroundMedia | undefined>;
  backgroundColumns?: Record<string, string | undefined>;
  backgroundResizeTypes?: Record<string, TMediaResizeType | undefined>;
  mediaColumns?: MediaColumn[];
  isAllValid: boolean;
  isFirstStepValid: boolean;
  isSecondStepValid: boolean;
  maxStep: STEP_CREATE_BATCH | STEP_CREATE_BATCH_V2;
  currentStep: STEP_CREATE_BATCH | STEP_CREATE_BATCH_V2;
  nameInputError: string | undefined;
  showMediaAlert: boolean;
  showTextAlert: boolean;
  showReload: boolean;
  openFileManager: boolean;
  compositions: TComposition[];
  templatesToUse: ITemplate[];
  currentCompositionId?: string;
  isFetchingMediaCols: boolean;
  templatesDataFilters: Partial<FiltersType>;
  addTemplateBlocked: boolean;
  compositionToReplace: TComposition | undefined;
  temporalDataFilters: Partial<FiltersType> | undefined;
  setOpenFileManager: Dispatch<SetStateAction<boolean>>;
  setShowMediaAlert: Dispatch<SetStateAction<boolean>>;
  setShowTextAlert: Dispatch<SetStateAction<boolean>>;
  setPreviewLoading: (previewLoading: boolean) => void;
  clearContext: () => void;
  saveBatchName: (newBatchName: string | undefined) => void;
  getStepStatus: (currentStep: number, idx: number) => StepStatus;
  getStepsDisabled: (stepIndex: number) => boolean;
  updateMaxStep: (newMaxStep: STEP_CREATE_BATCH | STEP_CREATE_BATCH_V2) => void;
  updateCurrentStep: (
    currentStep: STEP_CREATE_BATCH | STEP_CREATE_BATCH_V2,
  ) => void;
  setSelectedTemplate: Dispatch<SetStateAction<ITemplate | undefined>>;
  setTemplates: Dispatch<SetStateAction<ITemplate[]>>;
  setVariables: Dispatch<SetStateAction<Array<TCompositionVariables>>>;
  setBackgroundResizeTypes: (
    resizeType: Record<string, TMediaResizeType | undefined> | undefined,
  ) => void;
  setBackgroundMedias: (
    media: Record<string, TBackgroundMedia | undefined> | undefined,
  ) => void;
  setBackgroundColumns: (
    column: Record<string, string | undefined> | undefined,
  ) => void;
  setHasFixedBackground: (hasFixedBackground: boolean) => void;
  setShowReload: Dispatch<SetStateAction<boolean>>;
  useEditingAssetBatchEffect: (
    editingAssetBatch: TAssetBatch | undefined,
  ) => void;
  useTemplateBackgroundEffect: (
    canvas: fabric.Canvas | undefined,
    editingComposition: TComposition,
    isMediaMaskOn: boolean,
    showVariablesOn: boolean,
    selectedRow: any,
  ) => void;
  setRuleConditions: Dispatch<SetStateAction<TRuleCondition[]>>;
  setPreviewCount: Dispatch<SetStateAction<number>>;
  saveRuleConditions: (newRuleConditions: TRuleCondition[]) => void;
  setCompositions: Dispatch<SetStateAction<TComposition[]>>;
  setCurrentCompositionId: Dispatch<SetStateAction<string | undefined>>;
  setTemplatesDataFilters: Dispatch<SetStateAction<Partial<FiltersType>>>;
  replaceComposition: (editingComposition: TComposition) => void;
  setCompositionToReplace: (
    value: SetStateAction<TComposition | undefined>,
  ) => void;
  setTemporalDataFilters: (
    value: SetStateAction<Partial<FiltersType> | undefined>,
  ) => void;
  removeComposition: (compositionToRemove: TComposition) => void;
  removeBackgrounds: (compositionToRemove: TComposition) => void;
}

export type TCompositionVariables = {
  compositionId: string;
  variables: TVariable[];
};

type ContextProviderProps = {
  columns: string[];
  children: ReactNode;
  rows: FeedTblRow[];
  feedId: string;
};

const Context = createContext<ContextProps>({} as ContextProps);

const ContextProvider = ({
  children,
  columns,
  rows,
  feedId,
}: ContextProviderProps) => {
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [compositions, setCompositions] = useState<TComposition[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ITemplate>();
  const [variables, setVariables] = useState<TCompositionVariables[]>([]);

  const [nameInputError, setNameInputError] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [ruleConditions, setRuleConditions] = useState<TRuleCondition[]>([
    getEmptyRuleCondition(0),
  ]);
  const [previewCount, setPreviewCount] = useState<number>(0);
  const [batchName, setBatchName] = useState<string>();
  const [currentGroupNum, setCurrentGroupNum] = useState<number>(1);
  const [hasFixedBackground, setHasFixedBackground] = useState(false);
  // For AssetBatchDrawer START
  const [backgroundResizeTypes, setBackgroundResizeTypes] =
    useState<Record<string, TMediaResizeType | undefined>>();
  const [backgroundColumns, setBackgroundColumns] =
    useState<Record<string, string | undefined>>();
  const [backgroundMedias, setBackgroundMedias] =
    useState<Record<string, TBackgroundMedia | undefined>>();
  // For AssetBatchDrawer END
  const [currentCompositionId, setCurrentCompositionId] = useState<string>();
  const [templatesDataFilters, setTemplatesDataFilters] = useState<
    Partial<FiltersType>
  >({});
  const [maxStep, setMaxStep] = useState<
    STEP_CREATE_BATCH | STEP_CREATE_BATCH_V2
  >(STEP_CREATE_BATCH.ONE_CONDITION);
  const [currentStep, setCurrentStep] = useState<
    STEP_CREATE_BATCH | STEP_CREATE_BATCH_V2
  >(STEP_CREATE_BATCH.ONE_CONDITION);
  const [showMediaAlert, setShowMediaAlert] = useState<boolean>(true);
  const [showTextAlert, setShowTextAlert] = useState<boolean>(true);
  const { data: mediaColumns, isFetching: isFetchingMediaCols } =
    useGetMediaColumns(feedId, { rules: ruleConditions });
  const [showReload, setShowReload] = useState(false);
  const [openFileManager, setOpenFileManager] = useState(false);
  const [compositionToReplace, setCompositionToReplace] =
    useState<TComposition>();
  const [temporalDataFilters, setTemporalDataFilters] =
    useState<Partial<FiltersType>>();

  const updateMaxStep = useCallback(
    (newMaxStep: STEP_CREATE_BATCH | STEP_CREATE_BATCH_V2) => {
      setMaxStep(newMaxStep);
    },
    [],
  );
  const updateCurrentStep = useCallback(
    (currStep: STEP_CREATE_BATCH | STEP_CREATE_BATCH_V2) => {
      setCurrentStep(currStep);
    },
    [],
  );

  const saveBatchName = useCallback((newBatchName: string | undefined) => {
    setBatchName(newBatchName);
    if (newBatchName && newBatchName.trim() !== "") {
      setNameInputError(undefined);
      return;
    }
    setNameInputError("create asset batch name");
  }, []);

  const saveRuleConditions = useCallback(
    (newRuleConditions: TRuleCondition[]) => {
      updateMaxStep(currentStep);
      setRuleConditions(newRuleConditions);
    },
    [currentStep, updateMaxStep],
  );

  const clearContext = useCallback(() => {
    setCompositions([]);
    setTemplates([]);
    setSelectedTemplate(undefined);
    setSearchValue("");
    setBackgroundMedias(undefined);
    setBackgroundColumns(undefined);
    setBackgroundResizeTypes(undefined);
    setVariables([]);
    setRuleConditions([getEmptyRuleCondition(0)]);
    setPreviewCount(0);
    setBatchName(undefined);
    setCurrentGroupNum(1);
    setCurrentStep(STEP_CREATE_BATCH.ONE_CONDITION);
    setMaxStep(STEP_CREATE_BATCH.ONE_CONDITION);
    setNameInputError(undefined);
    setCurrentCompositionId(undefined);
    setTemplatesDataFilters({});
    setCompositionToReplace(undefined);
    setTemporalDataFilters(undefined);
  }, []);

  useEffect(() => {
    const contextMounted = () => {
      setLoading(false);
    };

    contextMounted();

    return () => {
      setLoading(true);
      setCompositions([]);
      setTemplates([]);
      setSelectedTemplate(undefined);
      setSearchValue("");
      setBackgroundMedias(undefined);
      setBackgroundColumns(undefined);
      setBackgroundResizeTypes(undefined);
      setVariables([]);
      setRuleConditions([getEmptyRuleCondition(0)]);
      setPreviewCount(0);
      setBatchName(undefined);
      setCurrentGroupNum(1);
      setCurrentStep(STEP_CREATE_BATCH.ONE_CONDITION);
      setMaxStep(STEP_CREATE_BATCH.ONE_CONDITION);
      setNameInputError(undefined);
      setCurrentCompositionId(undefined);
      setTemplatesDataFilters({});
      setCompositionToReplace(undefined);
      setTemporalDataFilters(undefined);
    };
  }, []);

  const isFirstStepValid =
    !!batchName?.trim() && isValidApplyCondition(ruleConditions, previewCount);
  const isSecondStepValid = compositions.length > 0;

  const getStepStatus = (currentStep: number, idx: number) => {
    if (currentStep === idx) return "process";
    if (currentStep < idx) return "wait";
    if (idx === 0 && isFirstStepValid) return "finish";
    if (idx === 1 && isSecondStepValid) return "finish";
    return "error";
  };

  const getStepsDisabled = (stepIndex: number) => {
    if (stepIndex === 1) return !isFirstStepValid;
    if (stepIndex === 2) return !isFirstStepValid || !isSecondStepValid;
    return false;
  };

  const isAllValid =
    isFirstStepValid &&
    compositions.length > 0 &&
    compositions.every(composition =>
      isValidDynamicText(
        composition.variables,
        variables.find(
          variable => variable.compositionId === composition.compositionId,
        )?.variables ?? [],
        templates.find(template => template.id === composition.template),
      ),
    );

  const useEditingAssetBatchEffect = (
    editingAssetBatch: TAssetBatch | undefined,
  ) => {
    useEffect(() => {
      if (editingAssetBatch) {
        saveBatchName(editingAssetBatch.name);
        saveRuleConditions(parseConditions(editingAssetBatch.conditions));
        setCompositions(editingAssetBatch.compositions);
        setVariables(
          parseCompositionToVariables(editingAssetBatch.compositions),
        );
        editingAssetBatch.compositions.forEach(composition => {
          setBackgroundResizeTypes(prev => ({
            ...prev,
            [composition.compositionId]: getBackgroundResizeType(
              composition.variables,
            ),
          }));
        });
      }
    }, [editingAssetBatch]);
  };

  const addTemplateBlocked = useMemo(
    () =>
      templates.length > 0 &&
      templates.some(template => !!template.htmlFileUrl),

    [templates],
  );

  const useTemplateBackgroundEffect = (
    canvas: fabric.Canvas | undefined,
    editingComposition: TComposition,
    isMediaMaskOn: boolean,
    showVariablesOn: boolean,
    selectedRow: any,
  ) => {
    useEffect(() => {
      const resetBackgroundImage = () => {
        canvas?.setBackgroundImage(new fabric.Image(""), () => {
          canvas.renderAll();
          setPreviewLoading(false);
        });
      };

      if (hasFixedBackground) {
        setPreviewLoading(false);
        return;
      }

      if (
        !backgroundMedias?.[editingComposition.compositionId] ||
        !isMediaMaskOn ||
        !showVariablesOn
      ) {
        resetBackgroundImage();
        return;
      }
      const { type, src } = backgroundMedias[editingComposition.compositionId]!;

      // remove theme background layer if exists
      const themeBg = canvas
        ?.getObjects()
        .find(obj => (obj as any).customType === "theme_background");
      if (themeBg) canvas?.remove(themeBg);

      const dummyTarget = new fabric.Rect({
        left: 0,
        top: 0,
        width: canvas?.vptCoords?.br?.x,
        height: canvas?.vptCoords?.br?.y,
      });

      // Set video background
      if (["mp4"].includes(type)) {
        const addVideoBackground = async () => {
          const videoEle = await createVideoElement(src, "mp4");
          const videoImage = new fabric.Image(videoEle, {
            left: 0,
            top: 0,
            selectable: false,
            evented: false,
            objectCaching: true,
            statefullCache: true,
            cacheProperties: ["videoTime"],
            type: "video",
          });

          const resizedVideo = getImageObjectWithResizeType(
            videoImage,
            dummyTarget,
            backgroundResizeTypes?.[editingComposition.compositionId] ?? "fill",
          );
          (resizedVideo as any).contentType = "video";
          canvas?.setBackgroundImage(resizedVideo, () =>
            setPreviewLoading(false),
          );
        };

        addVideoBackground();
        return;
      }

      // Set image background
      fabric.Image.fromURL(src, urlImg => {
        const img = urlImg.set({
          left: 0,
          top: 0,
          selectable: false,
        });

        const resizedImg = getImageObjectWithResizeType(
          img,
          dummyTarget,
          backgroundResizeTypes?.[editingComposition.compositionId] ?? "fill",
        );
        canvas?.setBackgroundImage(resizedImg, () => {
          canvas.renderAll();
          setPreviewLoading(false);
        });
      });
    }, [
      canvas,
      isMediaMaskOn,
      editingComposition.compositionId,
      showVariablesOn,
      selectedRow,
    ]);
  };

  const replaceComposition = useCallback(
    (editingComposition: TComposition) => {
      if (compositions.length === 1) {
        setTemporalDataFilters(templatesDataFilters);
        setTemplatesDataFilters({});
      }
      setSelectedTemplate(
        templates.find(template => template.id === editingComposition.template),
      );
      setOpenFileManager(true);
      setCompositionToReplace(editingComposition);
    },
    [
      compositions,
      setTemplatesDataFilters,
      setOpenFileManager,
      templatesDataFilters,
      templates,
    ],
  );

  const removeBackgrounds = (compositionToRemove: TComposition) => {
    setBackgroundMedias(backMedias =>
      replaceBackground(backMedias, compositionToRemove),
    );
    setBackgroundColumns(backCols =>
      replaceBackground(backCols, compositionToRemove),
    );
    setBackgroundResizeTypes(backResize =>
      replaceBackground(backResize, compositionToRemove),
    );
  };

  const removeComposition = (compositionToRemove: TComposition) => {
    setCompositions(oldCompositions =>
      removeItemByCompositionId(oldCompositions, compositionToRemove),
    );
    setVariables(oldVariables =>
      removeItemByCompositionId(oldVariables, compositionToRemove),
    );
    setTemplates(oldTemplates =>
      oldTemplates.filter(
        oldTemplate => oldTemplate.id !== compositionToRemove.template,
      ),
    );

    removeBackgrounds(compositionToRemove);
  };

  return (
    <Context.Provider
      value={{
        loading,
        previewLoading,
        searchValue,
        selectedTemplate,
        columns,
        rows,
        variables,
        feedId,
        ruleConditions,
        previewCount,
        batchName,
        currentGroupNum,
        compositions,
        backgroundMedias,
        backgroundColumns,
        backgroundResizeTypes,
        hasFixedBackground,
        mediaColumns,
        isAllValid,
        isFirstStepValid,
        isSecondStepValid,
        maxStep,
        currentStep,
        nameInputError,
        showMediaAlert,
        showTextAlert,
        showReload,
        openFileManager,
        isFetchingMediaCols,
        currentCompositionId,
        templatesToUse: templates,
        templatesDataFilters,
        addTemplateBlocked,
        temporalDataFilters,
        compositionToReplace,
        setOpenFileManager,
        setShowMediaAlert,
        setShowTextAlert,
        setPreviewLoading,
        clearContext,
        saveBatchName,
        getStepStatus,
        getStepsDisabled,
        updateMaxStep,
        updateCurrentStep,
        setSelectedTemplate,
        setTemplates,
        setVariables,
        setCompositions,
        setBackgroundColumns,
        setBackgroundMedias,
        setBackgroundResizeTypes,
        setHasFixedBackground,
        setShowReload,
        useEditingAssetBatchEffect,
        useTemplateBackgroundEffect,
        setRuleConditions,
        setPreviewCount,
        saveRuleConditions,
        setCurrentCompositionId,
        setTemplatesDataFilters,
        setCompositionToReplace,
        replaceComposition,
        setTemporalDataFilters,
        removeComposition,
        removeBackgrounds,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const AssetBatchesProvider = memo(ContextProvider);

export const useAssetBatchesContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error("Context must be used within a ContextProvider");
  }

  return context;
};
